import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Button, Container } from 'react-bootstrap';
import { Formik, FormikValues, yupToFormErrors } from 'formik';
import { merge } from 'lodash';

import history from 'browserHistory';
import { useModal } from 'modules/shared/hooks';
import FormikPatchTouched from 'modules/shared/Forms/FormikPatchTouched';
import DepartureData from 'modules/shared/DepartureData';
import FormikFillFields from 'modules/shared/Forms/FormikFillFields';
import FlightTop from 'modules/shared/FlightTop';
import FlightDetails from 'modules/shared/FlightDetails';
import ModalWrapper from 'modules/shared/ModalWrapper';
import { usePrepareValues } from 'modules/common/CommonTrip/hooks/usePrepareValues';
import { useValidationSchema } from 'modules/common/CommonTrip/hooks/useValidationSchema';
import YesNoModal from 'modules/shared/YesNoModal';
import ErrorMsg from 'modules/shared/ErrorMsg';
import Loader from 'modules/kiosk/components/Loader';
import RegOfDocuments from 'modules/shared/RegOfDocuments';
import DepartureComment from 'modules/shared/DepartureComment';
import {
  cleanError,
  cleanWarning,
  getTripRequest,
  putNextStepRequest,
  updateTripRequest,
} from 'modules/common/CommonTrip/actions';
import { commonTripSelector } from 'modules/common/CommonTrip/selectors';
import { deleteTab } from 'modules/common/Tabs/actions';
import { logoutSelector } from 'modules/common/LogoutBtn/selectors';

import { RootState } from 'rootReducer';
import DepartureUnloadingData from 'modules/shared/DepartureUnloadingData';
import { tripStatuses, tripIntentionStatuses } from '../../../../entities/Trip/constants';
import { DelayReason, DelayReasonService } from '../../../../entities/DelayReason';
import { cleanTripFields } from '../../../common/FillTrip';
import { addUserIDReguest } from './services/actions';
import { loadingSel } from './services/selectors';
import styles from './TripContainer.module.scss';
import TripStatusReasonModal from '../../../shared/TripStatusReasonModal/TripStatusReasonModal';

type Params = {
  tripId: string;
};

const TripContainer: React.FC = () => {
  const dispatch = useDispatch();
  const formRef = useRef();
  const { tripId = '' } = useParams<Params>();
  const { isShow, openModal, closeModal } = useModal();
  const { isShow: isShowDeleteOrders, openModal: openDeleteOrders, closeModal: closeDeleteOrders } = useModal();
  const { isShow: isShowYN, openModal: openYN, closeModal: closeYN } = useModal();
  const { isShow: isShowErr, openModal: openErrToast, closeModal: closeErrToast } = useModal();

  const [tripDataReq, setTripDataReq] = useState<any>();
  const [modalTitle, setModalTitle] = useState('Підтвердити');
  const [modalSuccess, setModalSuccess] = useState('Успішно');
  const [delayReasons, setDelayReasons] = useState<DelayReason[]>([]);
  const [delayReasonsLoading, setDelayReasonsLoading] = useState(false);
  const [delayReasonsError, setDelayReasonsError] = useState('');
  const [delayReasonTargetStatus, setDelayReasonTargetStatus] = useState<number | null>(null);
  const { isShow: isShowDelayReason, openModal: openDelayReason, closeModal: closeDelayReason } = useModal();

  const role = useSelector((state: RootState) => state.authModuleReducer.authReducer.role);

  const operatingProcedure = useMemo(() => {
    if (role?.branches && role.branches.length !== 0) {
      return role.branches.find((branch) => branch?.id === JSON.parse(localStorage.getItem('branchId') || 'null')?.id)
        ?.operatingProcedure;
    }

    return null;
  }, [role]);

  const { loadingTrip, errorTrip, trip, warning, warningText } = useSelector(commonTripSelector);
  const { id, status, ...rest } = trip;
  const statusId = status?.id || 2;
  const formStatusId = (() => {
    if (statusId === tripStatuses.waitingOrder) return tripStatuses.parkingInLine;
    if (statusId === tripStatuses.waitingInvoice) return tripStatuses.parking;
    return statusId;
  })();
  const isParkingStatus = statusId === tripStatuses.parking || statusId === tripStatuses.waitingInvoice;
  const wrongStatus = status?.id === 99;
  const wrongPermision = false;
  //  operatingProcedure === 'Порядок 2'
  //  ? status?.id === 99 || status?.id === 6 || status?.id === 8
  //  : status?.id === 6 || status?.id === 8 || status?.id === 10 || status?.id === 99;

  const loadingStatus = useSelector(loadingSel);
  const { branchTitle } = useSelector(logoutSelector);

  const initValues = usePrepareValues(id, trip); // need test

  // @ts-ignore
  const tripIntention = formRef.current?.values?.tripIntention;

  const isBlockFace = () => {
    // @ts-ignore
    if (!branchTitle || !branchTitle?.useFaceScan) return false;
    // @ts-ignore
    const driver = formRef.current?.values?.driver;

    if (driver && driver.id && driver.firstName?.length && !driver.isFaceId) return true;

    // @ts-ignore
    const forwarder = formRef.current?.values?.forwarder;

    if (forwarder && forwarder.id && forwarder?.firstName?.length && !forwarder.isFaceId) return true;

    return false;
  };

  const loading = +tripIntention === tripIntentionStatuses.loading;
  const useVisitorDoc: boolean = branchTitle?.useVisitorDoc ?? true;
  const useTransportDoc: boolean = branchTitle?.useTransportDoc ?? true;
  const validationSchema = useValidationSchema(
    'ordering',
    formStatusId,
    loading,
    operatingProcedure ?? '',
    useVisitorDoc,
    useTransportDoc,
  );

  const disabledPauseBtn =
    statusId === tripStatuses.waiting ||
    statusId === tripStatuses.downloading ||
    isParkingStatus ||
    statusId === tripStatuses.noOrder;
  const inProccess = statusId === tripStatuses.downloading || statusId === tripStatuses.waiting || isParkingStatus;

  let btnTextSecondary = 'Відкласти рейс';
  let btnTextRed = 'Поставити в чергу';

  if (statusId === tripStatuses.onPause) {
    btnTextSecondary = 'Поставити рейс на виїзд';
    btnTextRed = 'Зняти з паузи';
  }

  if (statusId === tripStatuses.onPause || inProccess) {
    btnTextSecondary = 'Зняти з паузи';
    btnTextRed = 'Поставити рейс на виїзд';
  }

  if (statusId === tripStatuses.create && (operatingProcedure === 'Порядок 2' || operatingProcedure === 'Порядок 3')) {
    btnTextRed = "Зареєструвати в'їзд";
  }

  const closeTabHandler = () => {
    const timer = setTimeout(() => {
      dispatch(deleteTab(`trip_${tripId}`));
      closeModal();
      if (tripIntention === 1) {
        return history.push('/ordering');
      }
      return history.push('/ordering/unload');
    }, 3000);

    clearTimeout(timer);
    dispatch(deleteTab(`trip_${tripId}`));
    closeModal();

    if (tripIntention === 1) {
      return history.push('/ordering');
    }
    return history.push('/ordering/unload');
  };

  const regDepartureHandler = () => {
    closeYN();

    if (operatingProcedure === 'Порядок 3' && tripDataReq?.statusId === tripStatuses.waitingDeparture) {
      dispatch(updateTripRequest(tripDataReq, false, status?.id, [tripStatuses.waitingDeparture, tripStatuses.left]));
    } else {
      dispatch(updateTripRequest(tripDataReq, false, status?.id));
    }

    openModal();

    return closeTabHandler();
  };

  const onFaceIdDriver = (type: string, value: any) => {
    // @ts-ignore
    if (rest?.driver?.id) dispatch(addUserIDReguest(rest.driver.id, type, value));
  };

  const delayHandler = (values: any) => {
    const tripData = merge({ id }, values);
    // @ts-ignore
    let newStatusId = tripStatuses.parkingInLine;

    if (statusId === tripStatuses.parkingInLine) {
      setModalTitle(`Перенести рейс ${id} на паузу`);
      setModalSuccess(`Рейс ${id} поставлено на паузу`);
      newStatusId = tripStatuses.noOrder;
    }
    if (statusId === tripStatuses.onPause || statusId === tripStatuses.noOrder) {
      setModalTitle(`Рейс ${id} зняти з паузи`);
      setModalSuccess(`Рейс ${id} знято з паузи`);
      newStatusId = tripStatuses.parkingInLine;
    }

    if (statusId === tripStatuses.parkingInLine) {
      newStatusId = tripStatuses.onPause;
    }

    tripData.statusId = newStatusId;

    setTripDataReq(tripData);
    openYN();
  };

  const waitingStatusHandler = async (values: any, newStatusId: number) => {
    const tripData = merge({ id }, values);

    if (newStatusId === tripStatuses.waitingOrder) {
      setModalTitle(`Рейс ${id} очікує наряд`);
      setModalSuccess(`Рейс ${id} переведено в очікування наряду`);
    }

    if (newStatusId === tripStatuses.waitingInvoice) {
      setModalTitle(`Рейс ${id} очікує накладну`);
      setModalSuccess(`Рейс ${id} переведено в очікування накладної`);
    }

    setDelayReasonTargetStatus(newStatusId);
    setDelayReasonsLoading(true);
    setDelayReasonsError('');
    try {
      const reasons = await DelayReasonService.getDelayReasons(newStatusId);
      setDelayReasons(reasons);
      setTripDataReq(tripData);
      openDelayReason();
    } catch (error: any) {
      setDelayReasonsError(error.message || 'Не вдалося отримати причини затримки');
    } finally {
      setDelayReasonsLoading(false);
    }
  };

  useEffect(() => {
    if (tripId && !id) dispatch(getTripRequest(tripId));
  }, [id]);

  useEffect(() => {
    if (errorTrip) {
      return openErrToast();
    }
    closeErrToast();
  }, [errorTrip]);

  const errMsg = errorTrip?.data?.message || errorTrip?.data?.ExceptionMessage;

  const validate = (v: any) => {
    try {
      validationSchema.validateSync(v, { abortEarly: false, context: { values: v } });
      return {};
    } catch (error) {
      // @ts-igonre
      console.log((error as any)?.errors);
      return yupToFormErrors<FormikValues>(error);
    }
  };

  return (
    <Container>
      {(loadingStatus || loadingTrip || !id) && <Loader />}
      {!loadingStatus && !loadingTrip && id && !wrongStatus && statusId && !wrongPermision ? (
        <Formik
          // @ts-ignore
          innerRef={formRef}
          initialValues={initValues}
          // validationSchema={validationSchema}
          validate={validate}
          validateOnMount
          enableReinitialize
          onSubmit={(values) => {
            const tripData = merge({ id }, values);

            const preStatus = values?.orders?.length === 0 ? tripStatuses.parkingInLine : tripStatuses.waiting;
            let newStatusId =
              operatingProcedure === 'Порядок 2' || operatingProcedure === 'Порядок 3'
                ? preStatus
                : tripStatuses.waiting;

            if (
              statusId === tripStatuses.parkingInLine ||
              statusId === tripStatuses.waitingOrder ||
              statusId === tripStatuses.noOrder
            ) {
              setModalTitle(`Рейс ${id} поставити в чергу`);
              setModalSuccess(`Рейс ${id} поставлено в чергу`);
            }

            if (statusId === tripStatuses.waitingOrder) {
              newStatusId = tripStatuses.waiting;
            }

            if (inProccess || statusId === tripStatuses.waitingInvoice || statusId === tripStatuses.onPause) {
              setModalTitle(`Дозволити рейсу ${id} виїзд`);
              setModalSuccess(`Рейс ${id} дозволено виїзд`);
              newStatusId = tripStatuses.waitingDeparture;
            }

            if (statusId === tripStatuses.create && tripIntention === 2) {
              newStatusId = tripStatuses.unloading;
            }
            tripData.statusId = newStatusId;

            setTripDataReq(tripData);
            openYN();
          }}
        >
          {({ values, handleChange, handleSubmit, setFieldValue, isValid, errors }) => {
            const isRulesOneOrdersBlocked = operatingProcedure === 'Порядок 1' && !values.orders && tripIntention === 1;
            const isFaceBlocked = isBlockFace();
            const isSubmitDisabled = isRulesOneOrdersBlocked || !isValid || isFaceBlocked;
            const isTripDocumentsVerified = values.isDriverDocumentValid && values.isTransportDocumentValid;
            const hasActiveOrder = values.orders?.some(
              (order: any) => !order?.isDeleted && !order?.isInputError && order?.number,
            );
            const isWaitingOrderDisabled = !isTripDocumentsVerified || isFaceBlocked;
            const isWaitingInvoiceDisabled = !isTripDocumentsVerified || !hasActiveOrder || isFaceBlocked;

            if (isSubmitDisabled) {
              console.log('ordering submit disabled', {
                operatingProcedure,
                statusId,
                tripIntention,
                isValid,
                isFaceBlocked,
                isRulesOneOrdersBlocked,
                errors,
                carrierType: values?.['сarrierType'],
                orders: values?.orders,
              });
            }

            return (
              <form onSubmit={handleSubmit} className="mt-4">
                <FormikPatchTouched />
                <FormikFillFields />
                <FlightTop
                  status={status}
                  tripId={id}
                  handleChange={handleChange}
                  tripIntention={values.tripIntention}
                  hideTripIntention
                />
                {tripIntention === 2 &&
                  (((operatingProcedure === 'Порядок 2' || operatingProcedure === 'Порядок 3') &&
                    (statusId === tripStatuses.create || statusId === tripStatuses.unloading)) ||
                    (operatingProcedure !== 'Порядок 2' &&
                      operatingProcedure !== 'Порядок 3' &&
                      statusId === tripStatuses.unloading)) && <DepartureUnloadingData values={values} />}

                {values.tripIntention === 1 && (
                  <RegOfDocuments mode="ordering" handleChange={handleChange} setFieldValue={setFieldValue} />
                )}
                <FlightDetails
                  mode="ordering"
                  statusId={formStatusId}
                  isBlockFace={isBlockFace}
                  tripType="driver"
                  handleChange={handleChange}
                  onFaceID={onFaceIdDriver}
                  operatingProcedure={operatingProcedure ?? ''}
                />
                <FlightDetails
                  mode="ordering"
                  statusId={formStatusId}
                  tripType="vehicle"
                  handleChange={handleChange}
                  operatingProcedure={operatingProcedure ?? ''}
                />
                <FlightDetails
                  mode="ordering"
                  statusId={formStatusId}
                  isBlockFace={isBlockFace}
                  tripType="forwarder"
                  onFaceID={onFaceIdDriver}
                  handleChange={handleChange}
                  operatingProcedure={operatingProcedure ?? ''}
                />

                {operatingProcedure === 'Порядок 3' &&
                  (statusId === tripStatuses.waiting ||
                    statusId === tripStatuses.downloading ||
                    isParkingStatus ||
                    statusId === tripStatuses.waitingDeparture) &&
                  tripIntention === 1 && (
                    <DepartureData
                      mode="security"
                      statusId={statusId}
                      values={values}
                      errors={errors}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                      allowWeight={0}
                      allowedExitRealTolerance={0}
                      allowedExitTheoreticalTolerance={0}
                      restStatus={false}
                      operatingProcedure={operatingProcedure ?? ''}
                    />
                  )}
                <DepartureComment mode="ordering" statusId={statusId} handleChange={handleChange} />
                {statusId !== tripStatuses.waitingAdmin && (
                  <div className={`d-flex justify-content-end mt-4 mb-5 ${styles.actionButtons}`}>
                    {statusId === tripStatuses.parkingInLine && tripIntention === tripIntentionStatuses.loading && (
                      <Button
                        onClick={() => waitingStatusHandler(values, tripStatuses.waitingOrder)}
                        className="px-3 text-uppercase mr-3"
                        variant="secondary"
                        disabled={isWaitingOrderDisabled}
                      >
                        <h5 className="my-2 mx-5 text-white">Очікування наряду</h5>
                      </Button>
                    )}
                    {statusId === tripStatuses.parking && tripIntention === tripIntentionStatuses.loading && (
                      <Button
                        onClick={() => waitingStatusHandler(values, tripStatuses.waitingInvoice)}
                        className="px-3 text-uppercase mr-3"
                        variant="secondary"
                        disabled={isWaitingInvoiceDisabled}
                      >
                        <h5 className="my-2 mx-5 text-white">Очікування накладної</h5>
                      </Button>
                    )}
                    {statusId === tripStatuses.waiting || statusId === tripStatuses.downloading || isParkingStatus ? (
                      <Button
                        onClick={() => {
                          if (values?.orders?.length !== 0) {
                            openDeleteOrders();
                          } else {
                            dispatch(putNextStepRequest(values));
                            closeTabHandler();
                          }
                        }}
                        className="px-3 text-uppercase mr-3"
                        variant="secondary"
                      >
                        <h5 className="my-2 mx-5 text-white">Зняти з електронної черги</h5>
                      </Button>
                    ) : null}
                    {statusId !== tripStatuses.noOrder &&
                      !disabledPauseBtn &&
                      tripIntention === 1 &&
                      statusId !== tripStatuses.create &&
                      operatingProcedure === 'Порядок 1' && (
                        <Button
                          onClick={() => delayHandler(values)}
                          className="px-3 text-uppercase mr-3"
                          variant="secondary"
                          disabled={isBlockFace()}
                        >
                          <h5 className="my-2 mx-5 text-white">{btnTextSecondary}</h5>
                        </Button>
                      )}
                    {(statusId === tripStatuses.parkingInLine || statusId === tripStatuses.waitingOrder) &&
                      tripIntention === tripIntentionStatuses.loading &&
                      operatingProcedure === 'Порядок 3' && (
                        <Button
                          disabled={!isValid || isBlockFace()}
                          onClick={() => {
                            const updateTripData = merge({ id }, { ...values, isNotLoaded: true, statusId: 99 });

                            dispatch(updateTripRequest(updateTripData, false, status?.id));

                            dispatch(cleanTripFields());
                            return closeTabHandler();
                          }}
                          className="px-3 text-uppercase mr-3"
                          variant="secondary"
                        >
                          <h5 className="my-2 mx-5 text-white">Виїзд без завантаження</h5>
                        </Button>
                      )}
                    {statusId === tripStatuses.unloading &&
                      tripIntention === tripIntentionStatuses.unloading &&
                      operatingProcedure === 'Порядок 3' && (
                        <Button
                          disabled={!isValid || isBlockFace()}
                          onClick={() => {
                            const updateTripData = merge({ id }, { ...values, statusId: 99 });

                            dispatch(updateTripRequest(updateTripData, false, status?.id));

                            dispatch(cleanTripFields());
                            return closeTabHandler();
                          }}
                          className="px-3 text-uppercase btn btn-danger"
                          variant="secondary"
                        >
                          <h5 className="my-2 mx-5 text-white">Зареєструвати виїзд</h5>
                        </Button>
                      )}
                    {(tripIntention === 1 ||
                      (statusId === tripStatuses.create && operatingProcedure !== 'Порядок 1')) && (
                      <Button
                        type="submit"
                        className="px-3 text-uppercase"
                        variant="danger"
                        disabled={isSubmitDisabled}
                      >
                        <h5 className="my-2 text-white mx-5">{btnTextRed}</h5>
                      </Button>
                    )}
                  </div>
                )}
              </form>
            );
          }}
        </Formik>
      ) : null}

      {wrongPermision ? (
        <h3 className="m-5 text-center">
          Порушення доступу до деталей рейсу.{' '}
          <button
            className={styles.button}
            onClick={() => {
              dispatch(deleteTab(`trip_${tripId}`));
              if (tripIntention === 1) {
                history.push('/ordering');
              } else {
                history.push('/ordering/unload');
              }
            }}
            type="button"
          >
            <h3 className={styles.text}>На головну</h3>
          </button>
        </h3>
      ) : null}

      <ModalWrapper show={isShow} onHide={closeTabHandler}>
        <div>
          <h6 className="text-center">{modalSuccess}</h6>
          <div className="d-flex justify-content-center mt-3">
            <Button className="px-5" variant="outline-secondary" onClick={closeTabHandler}>
              Ok
            </Button>
          </div>
        </div>
      </ModalWrapper>

      <ModalWrapper show={isShowDeleteOrders} onHide={closeDeleteOrders}>
        <div>
          <h6 className="text-center mb-5">Для зняття рейса з електронної черги необхідно видалити наряди.</h6>
          <div className="d-flex justify-content-center mt-3">
            <Button className="px-5" variant="outline-secondary" onClick={closeDeleteOrders}>
              Ok
            </Button>
          </div>
        </div>
      </ModalWrapper>

      <YesNoModal isShow={isShowYN} title={modalTitle} acceptHandler={regDepartureHandler} closeModal={closeYN} />

      <TripStatusReasonModal
        isShow={isShowDelayReason}
        reasons={delayReasons}
        loading={delayReasonsLoading}
        error={delayReasonsError}
        title={modalTitle}
        isStatusConfirmation
        statusAcceptHandler={() => {
          closeDelayReason();
          regDepartureHandler();
        }}
        closeModal={closeDelayReason}
        acceptHandler={(comment) => {
          if (!tripDataReq || !delayReasonTargetStatus) return;
          const field =
            delayReasonTargetStatus === tripStatuses.waitingOrder ? 'delayReasonsOrder' : 'delayReasonsInvoice';
          tripDataReq[field] = comment.commentText;
          tripDataReq.statusId = delayReasonTargetStatus;
          setTripDataReq(tripDataReq);
        }}
      />

      <ModalWrapper show={warning} onHide={() => dispatch(cleanWarning())}>
        <div className="d-flex justify-content-center mb-3">
          <h3>Увага</h3>
        </div>
        <div>
          {warningText?.map((text: any) => (
            <h6 className="text-center mb-3">{text}</h6>
          ))}
          <div className="d-flex justify-content-center mt-4">
            <Button className="px-5" variant="outline-secondary" onClick={() => dispatch(cleanWarning())}>
              Ok
            </Button>
          </div>
        </div>
      </ModalWrapper>

      <ErrorMsg
        isShow={isShowErr}
        closeHandler={() => {
          closeErrToast();
          cleanError();
        }}
        error={errMsg || `HTTP:${errorTrip?.status}. Виникла помилка`}
      />
    </Container>
  );
};

export default TripContainer;
